import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FiChevronDown, FiCheck } from "react-icons/fi";

const GeneratorDropdown = ({
  generators,
  selectedGenerator,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedGen = generators.find((g) => g.id === selectedGenerator);
  const displayText = selectedGen ? selectedGen.name : "Select Generator";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex w-full items-center justify-between rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-left transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={
            !selectedGenerator ? "text-gray-400" : "font-semibold text-gray-900"
          }
        >
          {displayText}
        </span>
        <FiChevronDown
          className={`size-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-xl">
          <div className="max-h-80 overflow-y-auto p-2">
            {generators.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No generators available
              </div>
            ) : (
              generators.map((generator) => (
                <button
                  key={generator.id}
                  type="button"
                  onClick={() => {
                    onChange(generator.id);
                    setIsOpen(false);
                  }}
                  className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors hover:bg-primary-50"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {generator.name}
                    </span>
                    {generator.endpoint_url && (
                      <span className="mt-0.5 text-xs text-gray-500">
                        {generator.endpoint_url.length > 50
                          ? generator.endpoint_url.substring(0, 50) + "..."
                          : generator.endpoint_url}
                      </span>
                    )}
                  </div>
                  {selectedGenerator === generator.id && (
                    <FiCheck className="size-5 text-primary-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

GeneratorDropdown.propTypes = {
  generators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      endpoint_url: PropTypes.string,
    })
  ).isRequired,
  selectedGenerator: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

GeneratorDropdown.defaultProps = {
  disabled: false,
};

export default GeneratorDropdown;
